import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import '@material/web/switch/switch.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import 'vanilla-colorful/hex-color-picker.js'

const FONT_SIZES = [
  { value: 9, label: '9' },
  { value: 11, label: '11' },
  { value: 13, label: '13' },
  { value: 16, label: '16' },
  { value: 20, label: '20' },
  { value: 24, label: '24' },
]

const FONTS = [
  { value: 'sans-serif', label: 'Sans Serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'monospace', label: 'Monospace' },
  { value: 'cursive', label: 'Cursive' },
]

const PRESET_COLORS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
  '#ffffff',
  '#94a3b8',
  '#64748b',
  '#475569',
  '#334155',
  '#1e293b',
  '#000000',
]

@customElement('label-editor-dropdown')
export class LabelEditorDropdown extends LitElement {
  static override styles = css`
    :host {
      position: absolute;
      z-index: 2000;
    }

    .dropdown {
      background: rgba(30, 41, 59, 0.95);
      backdrop-filter: blur(12px);
      border-radius: 12px;
      padding: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      width: 264px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      overflow-y: visible;
    }

    .row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    .row-label {
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      font-weight: 500;
    }

    md-outlined-text-field {
      width: 100%;
      --md-outlined-text-field-container-shape: 8px;
      --md-outlined-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      --md-outlined-field-focus-outline-color: rgba(255, 255, 255, 0.7);
      --md-outlined-text-field-input-text-color: white;
      --md-outlined-text-field-label-text-color: rgba(255, 255, 255, 0.6);
      --md-outlined-text-field-input-text-size: 13px;
      --md-sys-color-on-surface-variant: rgba(255, 255, 255, 0.6);
      --md-sys-color-on-surface: white;
    }

    md-outlined-select {
      width: 100%;
      --md-outlined-select-text-field-container-height: 40px;
      --md-outlined-select-text-field-container-shape: 8px;
      --md-outlined-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      --md-outlined-field-focus-outline-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-input-text-color: white;
      --md-outlined-select-text-field-label-text-color: rgba(255, 255, 255, 0.6);
      --md-outlined-select-text-field-trailing-icon-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-input-text-size: 13px;
      --md-sys-color-surface-container: rgba(30, 41, 59, 0.98);
      --md-sys-color-on-surface: white;
      --md-sys-color-on-surface-variant: rgba(255, 255, 255, 0.6);
    }

    md-switch {
      --md-switch-selected-track-color: #3f5a7a;
      --md-switch-selected-handle-color: white;
      --md-switch-selected-icon-color: #3f5a7a;
      --md-switch-unselected-track-color: rgba(255, 255, 255, 0.15);
      --md-switch-unselected-handle-color: rgba(255, 255, 255, 0.5);
    }

    .divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.12);
      margin: 2px 0;
    }

    .preset-grid {
      display: grid;
      grid-template-columns: repeat(8, 1fr);
      gap: 4px;
    }

    .preset-swatch {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      border: 2px solid transparent;
      cursor: pointer;
      padding: 0;
      transition:
        border-color 0.15s ease,
        transform 0.1s ease;
    }

    .preset-swatch:hover {
      transform: scale(1.15);
    }

    .preset-swatch.selected {
      border-color: white;
    }

    .preset-swatch[data-color='#ffffff'],
    .preset-swatch[data-color='#000000'] {
      border-color: rgba(255, 255, 255, 0.3);
    }

    .preset-swatch.selected[data-color='#ffffff'],
    .preset-swatch.selected[data-color='#000000'] {
      border-color: white;
    }

    hex-color-picker {
      width: 100%;
      height: 140px;
    }

    hex-color-picker::part(saturation) {
      border-radius: 6px 6px 0 0;
    }

    hex-color-picker::part(hue) {
      border-radius: 0 0 6px 6px;
      height: 14px;
    }

    hex-color-picker::part(saturation-pointer),
    hex-color-picker::part(hue-pointer) {
      width: 18px;
      height: 18px;
    }

    .hex-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .hex-label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 11px;
      font-weight: 500;
    }

    .hex-input {
      flex: 1;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      color: white;
      font-size: 13px;
      font-family: monospace;
      padding: 5px 8px;
      outline: none;
    }

    .hex-input:focus {
      border-color: rgba(255, 255, 255, 0.4);
    }

    .toggle-group {
      display: flex;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .toggle-btn {
      flex: 1;
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      padding: 8px 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s;
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
    }

    .toggle-btn:not(:last-child) {
      border-right: 1px solid rgba(255, 255, 255, 0.2);
    }

    .toggle-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.8);
    }

    .toggle-btn.active {
      background: #3f5a7a;
      color: white;
    }

    .align-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }
  `

  @property({ type: String })
  labelText = ''

  @property({ type: String })
  labelColor = '#ffffff'

  @state()
  private hexInput = '#ffffff'

  @property({ type: Boolean })
  labelVisible = true

  @property({ type: String })
  verticalAlign: 'top' | 'middle' | 'bottom' = 'bottom'

  @property({ type: String })
  horizontalAlign: 'left' | 'center' | 'right' = 'center'

  @property({ type: Number })
  fontSize = 11

  @property({ type: String })
  fontFamily = 'sans-serif'

  override connectedCallback(): void {
    super.connectedCallback()
    this.hexInput = this.labelColor
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('labelColor') && !this.shadowRoot?.querySelector('.hex-input:focus')) {
      this.hexInput = this.labelColor
    }
  }

  private handleTextInput(e: Event): void {
    const input = e.target as HTMLInputElement
    this.labelText = input.value
    this.dispatchChange()
  }

  private handleVisibleChange(e: Event): void {
    const sw = e.target as HTMLInputElement & { selected: boolean }
    this.labelVisible = sw.selected
    this.dispatchChange()
  }

  private handlePresetClick(color: string): void {
    this.labelColor = color
    this.hexInput = color
    this.dispatchChange()
    const picker = this.shadowRoot?.querySelector('hex-color-picker') as
      | (HTMLElement & {
          color: string
        })
      | null
    if (picker) {
      picker.color = color
    }
  }

  private handlePickerChange(e: CustomEvent<{ value: string }>): void {
    const color = e.detail.value
    this.labelColor = color
    this.hexInput = color
    this.dispatchChange()
  }

  private handleHexInput(e: Event): void {
    const input = e.target as HTMLInputElement
    let val = input.value.trim()
    this.hexInput = val
    if (!val.startsWith('#')) {
      val = '#' + val
    }
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      this.labelColor = val
      this.dispatchChange()
      const picker = this.shadowRoot?.querySelector('hex-color-picker') as
        | (HTMLElement & {
            color: string
          })
        | null
      if (picker) {
        picker.color = val
      }
    }
  }

  private handleVerticalAlign(val: 'top' | 'middle' | 'bottom'): void {
    this.verticalAlign = val
    this.dispatchChange()
  }

  private handleHorizontalAlign(val: 'left' | 'center' | 'right'): void {
    this.horizontalAlign = val
    this.dispatchChange()
  }

  private handleFontSizeChange(e: Event): void {
    const select = e.target as HTMLSelectElement
    if (select.value) {
      this.fontSize = parseInt(select.value)
      this.dispatchChange()
    }
  }

  private handleFontFamilyChange(e: Event): void {
    const select = e.target as HTMLSelectElement
    if (select.value) {
      this.fontFamily = select.value
      this.dispatchChange()
    }
  }

  private dispatchChange(): void {
    this.dispatchEvent(
      new CustomEvent('label-changed', {
        detail: {
          text: this.labelText,
          fontColor: this.labelColor,
          visible: this.labelVisible,
          verticalAlign: this.verticalAlign,
          horizontalAlign: this.horizontalAlign,
          fontSize: this.fontSize,
          fontFamily: this.fontFamily,
        },
        bubbles: true,
      })
    )
  }

  override render(): unknown {
    return html`
      <div class="dropdown">
        <md-outlined-text-field
          label="Label"
          .value=${this.labelText}
          @input=${this.handleTextInput}
        ></md-outlined-text-field>

        <md-outlined-select
          label="Font"
          .value=${this.fontFamily}
          @change=${this.handleFontFamilyChange}
        >
          ${FONTS.map(
            (f): unknown => html`
              <md-select-option value=${f.value} ?selected=${this.fontFamily === f.value}>
                <div slot="headline" style="font-family: ${f.value};">${f.label}</div>
              </md-select-option>
            `
          )}
        </md-outlined-select>
        <md-outlined-select
          label="Size"
          .value=${String(this.fontSize)}
          @change=${this.handleFontSizeChange}
        >
          ${FONT_SIZES.map(
            (s): unknown => html`
              <md-select-option value=${String(s.value)} ?selected=${this.fontSize === s.value}>
                <div slot="headline">${s.label}</div>
              </md-select-option>
            `
          )}
        </md-outlined-select>

        <div class="row">
          <span class="row-label">Show Label</span>
          <md-switch ?selected=${this.labelVisible} @change=${this.handleVisibleChange}></md-switch>
        </div>

        <div class="divider"></div>

        <div class="align-row">
          <div class="toggle-group">
            <button
              class="toggle-btn ${this.verticalAlign === 'top' ? 'active' : ''}"
              @click=${(): void => this.handleVerticalAlign('top')}
              title="Top"
            >
              vertical_align_top
            </button>
            <button
              class="toggle-btn ${this.verticalAlign === 'middle' ? 'active' : ''}"
              @click=${(): void => this.handleVerticalAlign('middle')}
              title="Middle"
            >
              vertical_align_center
            </button>
            <button
              class="toggle-btn ${this.verticalAlign === 'bottom' ? 'active' : ''}"
              @click=${(): void => this.handleVerticalAlign('bottom')}
              title="Bottom"
            >
              vertical_align_bottom
            </button>
          </div>
          <div class="toggle-group">
            <button
              class="toggle-btn ${this.horizontalAlign === 'left' ? 'active' : ''}"
              @click=${(): void => this.handleHorizontalAlign('left')}
              title="Left"
            >
              align_horizontal_left
            </button>
            <button
              class="toggle-btn ${this.horizontalAlign === 'center' ? 'active' : ''}"
              @click=${(): void => this.handleHorizontalAlign('center')}
              title="Center"
            >
              align_horizontal_center
            </button>
            <button
              class="toggle-btn ${this.horizontalAlign === 'right' ? 'active' : ''}"
              @click=${(): void => this.handleHorizontalAlign('right')}
              title="Right"
            >
              align_horizontal_right
            </button>
          </div>
        </div>

        <div class="divider"></div>

        <div class="preset-grid">
          ${PRESET_COLORS.map(
            (c): unknown => html`
              <button
                class="preset-swatch ${c === this.labelColor ? 'selected' : ''}"
                style="background: ${c}"
                data-color=${c}
                @click=${(): void => this.handlePresetClick(c)}
              ></button>
            `
          )}
        </div>

        <hex-color-picker
          .color=${this.labelColor}
          @color-changed=${this.handlePickerChange}
        ></hex-color-picker>

        <div class="hex-row">
          <span class="hex-label">HEX</span>
          <input
            class="hex-input"
            .value=${this.hexInput}
            @input=${this.handleHexInput}
            maxlength="7"
            placeholder="#000000"
          />
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'label-editor-dropdown': LabelEditorDropdown
  }
}
